import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/** Three or more underscores = one blank (supports ___ or ____) */
export const BLANK_SPLIT_RE = /_{3,}/;

/** Number of blank slots in the template */
export function countFillInBlanks(template) {
  if (!template || typeof template !== 'string') return 0;
  return (template.match(/_{3,}/g) || []).length;
}

/**
 * Resolves per-blank units from stored JSON. Supports legacy single `unit` (applies to every blank).
 * @param {object} payload parsed JSON
 * @param {number} n number of blanks
 * @returns {string[]} length n
 */
export function effectiveUnitsArray(payload, n) {
  if (n < 1) return [];
  if (!payload || typeof payload !== 'object') return Array(n).fill('');
  if (Array.isArray(payload.units)) {
    const mapped = payload.units.map((u) => String(u ?? '').trim());
    if (mapped.length >= n) return mapped.slice(0, n);
    const out = [...mapped];
    while (out.length < n) out.push('');
    return out;
  }
  const legacy = String(payload.unit ?? '').trim();
  return Array(n).fill(legacy);
}

/**
 * @param {object} props
 * @param {string} props.template
 * @param {string} props.value JSON string { blanks: string[], units?: string[], unit?: string (legacy) }
 * @param {(json: string) => void} props.onChange
 * @param {boolean} props.includeUnit
 * @param {string[]} props.units allowed unit labels
 */
export default function FillInBlanksAnswerField({
  template,
  value,
  onChange,
  includeUnit,
  units = [],
}) {
  const { t } = useTranslation();
  const parts = useMemo(() => (template || '').split(BLANK_SPLIT_RE), [template]);
  const n = countFillInBlanks(template);

  const parsed = useMemo(() => {
    try {
      const o = JSON.parse(value || '{}');
      if (o && Array.isArray(o.blanks)) {
        const blanks = [...o.blanks];
        const unitList = effectiveUnitsArray(o, n);
        return { blanks, units: unitList };
      }
    } catch {
      /* ignore */
    }
    return { blanks: [], units: Array(n).fill('') };
  }, [value, n]);

  const blanks = useMemo(() => {
    const arr = [...parsed.blanks];
    while (arr.length < n) arr.push('');
    while (arr.length > n) arr.pop();
    return arr;
  }, [parsed.blanks, n]);

  const unitsState = useMemo(() => {
    const arr = [...parsed.units];
    while (arr.length < n) arr.push('');
    while (arr.length > n) arr.pop();
    return arr;
  }, [parsed.units, n]);

  const emit = (nextBlanks, nextUnits) => {
    const payload = { blanks: nextBlanks };
    if (includeUnit) {
      payload.units = nextUnits;
    }
    onChange(JSON.stringify(payload));
  };

  if (n < 1) {
    return (
      <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
        {t('answers.fillInBlanksInvalidTemplate')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline gap-x-1 gap-y-2 text-base sm:text-lg leading-relaxed text-gray-800">
        {parts.map((part, i) => (
          <React.Fragment key={`part-${i}`}>
            {part !== '' && (
              <span className="whitespace-pre-wrap break-words">{part}</span>
            )}
            {i < n && (
              <span className="inline-flex flex-wrap items-baseline gap-1.5 align-baseline">
                <input
                  type="text"
                  autoComplete="off"
                  className="inline-block min-w-[5rem] max-w-[12rem] border-b-2 border-emerald-500 bg-emerald-50/50 px-2 py-1 text-center font-semibold text-emerald-900 rounded-t focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  value={blanks[i] ?? ''}
                  onChange={(e) => {
                    const next = [...blanks];
                    next[i] = e.target.value;
                    emit(next, unitsState);
                  }}
                  aria-label={t('answers.fillInBlankSlot', { index: i + 1 })}
                />
                {includeUnit && units.length > 0 && (
                  <select
                    className="inline-block min-w-[6rem] max-w-[10rem] rounded-lg border-2 border-gray-200 bg-white px-2 py-1 text-sm font-medium text-gray-800 focus:border-green-500 focus:ring-2 focus:ring-green-500"
                    value={unitsState[i] ?? ''}
                    onChange={(e) => {
                      const nextU = [...unitsState];
                      nextU[i] = e.target.value;
                      emit(blanks, nextU);
                    }}
                    aria-label={t('answers.fillInBlankUnit', { index: i + 1 })}
                  >
                    <option value="">{t('answers.selectUnitPlaceholder')}</option>
                    {units.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
